/*
 *   Copyright (c) 2020 BuserNet Consulting LLC
 *   All rights reserved.
 */
import _forEach from 'lodash/forEach';
import DefConst from '../constants/def.constant';
import ChartConst from '../constants/chart.constant';
import EventConst from '../constants/event.constant';
import utilityService from './utility.service';
import windbarbService from './windbarb.service';
import bnChartDataProcessService from './chartDataProcess.service';
import ConfigApplicationSettings from '../config/application.config';
import router from '../router';
import Emitter from '../bus';
import d3 from '../globalexports/d3.export';
import AppConst from '../constants/app.constant';
import {
  roundOfNumber,
} from './utils/roundOfNumber';
import { getTempLabelValue } from './chartData/getTempLabelValue';
import { Canvg } from 'canvg';

const bnd3Service = () => {
  const contents = {};

  function calcSelectionHeight(selectionElement) {
    const calculatedValue = selectionElement.node()
      ? selectionElement.node().getBoundingClientRect().height
      : 0;
    return calculatedValue;
  }

  function calcSelectionWidth(selectionElement) {
    const calculatedValue = selectionElement.node()
      ? selectionElement.node().getBoundingClientRect().width
      : 0;
    return calculatedValue;
  }

  contents.getSelectionHeight = (selectionElement) => calcSelectionHeight(selectionElement);

  contents.getSelectionWidth = (selectionElement) => calcSelectionWidth(selectionElement);

  contents.getFlightDotColor = (frl) => (
    DefConst.rulesetColorMapping[frl]
    || ChartConst.flightRuleColorIfNull
  );

  contents.getFlightDotText = (frl) => DefConst.rulesetLblMapping[frl] || '--';

  contents.getFlightDotColorForRruleset = (frl) => (
    DefConst.rulesetColorMappingRruleset[frl]
    || ChartConst.flightRuleColorIfNull
  );
  contents.getFlightDotTextForRruleset = (frl) => DefConst.rulesetLblMappingRruleset[frl] || '--';
  contents.processChartComponents = (
    bncharts,
    chartHeight,
    margin,
    svgHeight,
    width,
    yAxisRectWidth,
    bigChartHeight,
    yAxisRightRectWidth,
  ) => {
    // Positioning first-ticks of all charts inside the graph if they are going out
    bncharts.selectAll('.y').selectAll('.tick').filter((...args) => args[1] === 0).attr('transform', (...args) => {
      let x;
      let y;
      const selfEle = utilityService.getSelfElement(args);
      const useBigHeight = d3.select(selfEle.parentNode.parentNode).attr('use-big-height');
      const positionXY = utilityService.getTransformation(d3.select(selfEle).attr(
        'transform',
      ));
      const tickHeight = calcSelectionHeight(d3.select(selfEle).select('text'));
      const minValueOffset = (useBigHeight
        ? bigChartHeight
        : chartHeight) - (tickHeight / 2);
      if (positionXY.translateY > minValueOffset) {
        x = positionXY.translateX;
        y = minValueOffset;
        return `translate(${x},${y})`;
      }
      return d3.select(selfEle).attr('transform');
    });
    // Positioning last-ticks of all charts inside the graph if they are going out
    bncharts.selectAll('.y').selectAll('.tick:last-of-type')
      .attr('transform', (...args) => {
        let x;
        let y;
        const selfEle = utilityService.getSelfElement(args);
        const positionXY = utilityService.getTransformation(d3.select(selfEle).attr(
          'transform',
        ));
        const tickHeight = calcSelectionHeight(d3.select(selfEle).select('text'));
        const minValueOffset = tickHeight / 2;
        if (positionXY.translateY < minValueOffset) {
          x = positionXY.translateX;
          y = minValueOffset;
          return `translate(${x},${y})`;
        }
        return d3.select(selfEle).attr('transform');
      });
    // Alligning y-tick label to left
    bncharts.selectAll('.y-left .tick text')
      .attr('x', (...args) => {
        const selfEle = utilityService.getSelfElement(args);
        const tickWidth = calcSelectionWidth(d3.select(selfEle));
        const barbPadding = utilityService.getBarbPadding();
        return (yAxisRectWidth - barbPadding - tickWidth);
      });

    bncharts.selectAll('.y-right .tick text')
      .attr('x', () => -2);

    bncharts.append('rect')
      .attr('class', 'crop-rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', margin.left)
      .attr('height', svgHeight);
    bncharts.append('rect')
      .attr('class', 'crop-rect')
      .attr('x', width + margin.left)
      .attr('y', 0)
      .attr('width', margin.left)
      .attr('height', svgHeight);
    // This will prevent y-ticks line from overlapping on y-axis area
    bncharts.selectAll('.y-grid-line-container line')
      .attr('x2', width - (yAxisRightRectWidth || yAxisRectWidth))
      .attr('x1', () => yAxisRectWidth);

    bncharts.selectAll('.y-left-tick-highlighter line')
      .attr('x2', yAxisRectWidth)
      .attr('x1', () => {
        const barbPadding = utilityService.getBarbPadding() - ChartConst.yTickLineOffset;
        return yAxisRectWidth - barbPadding;
      });
  };
  contents.hideD3ToolTip = () => {
    d3.select('#d3tipcontent').style('opacity', '0');
    d3.select('#d3tipcontent').style('pointer-events', 'none');
  };
  contents.hideTrendLineOnClickOutside = () => {
    // Hide trendline when clicked outside
    d3.select('#main-content').on('click', () => {
      // d3.select("#route-trendLineContainer").style("opacity", 0);
      d3.selectAll('.part-of-tooltip').style('display', 'none');
      if (document.getElementById('meteo-barbs-container')) {
        d3.selectAll('.meteo-aloft-barb').style('display', 'none');
      }
      if (document.getElementById('route-barbs-container') && document.getElementById(
        'aloftBarbsCanvas-route',
      )) {
        d3.selectAll('.route-aloft-barb').style('display', 'none');
      }
      d3.selectAll('.airport-stop').select('rect').style('opacity', 0);
      d3.selectAll('.airport-stop').selectAll('text').style('fill', '#000');
      contents.hideD3ToolTip();
      contents.handleSliderToggleOnOutsideTap();
    });
  };
  contents.handleSliderToggleOnOutsideTap = () => {
    const tourContainer = document.getElementById('tour-container-overlay');
    if (tourContainer.style.display === 'none') {
      if (document.getElementsByClassName('app-etd-slider')[0] && d3.select('.app-etd-slider').style(
        'opacity',
      ) === '1') {
        Emitter.emit(EventConst.tapped_outside);
      }
    }
  };

  contents.addBarbMarker = (container, id, defaultWindSpeed, isAloftBarbHead, orient = 'auto') => {
    container.append('defs')
      .append('marker')
      .attr('id', id)
      .attr('viewBox', '0 0 10 10')
      .attr('orient', orient)
      .attr('markerHeight', ChartConst.barbhead.light.markerHeight)
      .attr('markerWidth', ChartConst.barbhead.light.markerWidth)
      .attr('refX', 9.6)
      .attr('refY', 7.5)
      .append('polyline')
      .attr('id', `${id}-polyline`)
      .attr('points', '0,0 10,5 0,10 1,5')
      .attr('transform', 'rotate(180 5 5) scale(0.5)')
      .attr('fill', isAloftBarbHead
        ? ChartConst.aloftBarbInitialColor
        : defaultWindSpeed.color);
  };

  contents.convertSvgToCanvas = (
    sourceSvgId,
    canvasContainerId,
    canvasId,
    canvasWidth,
    canvasHeight,
    callback,
  ) => {
    const serializer = new window.XMLSerializer();
    const svgNode = d3.select(`#${sourceSvgId}`).node();
    const devicePixelRatio = 1;
    d3.select(`#${canvasContainerId}`).append('canvas')
      .attr('id', canvasId)
      .attr('width', (canvasWidth) * devicePixelRatio)
      .attr('height', (canvasHeight) * devicePixelRatio)
      .style('position', 'absolute');
    if (svgNode) {
      const cv = document.getElementById(canvasId);
      const ctx = cv.getContext('2d');
      if (ctx) {
        const pixelRatio = window.devicePixelRatio || 1;
        cv.style.width = `${cv.width}px`;
        cv.style.height = `${cv.height}px`;
        cv.width *= pixelRatio;
        cv.height *= pixelRatio;
        ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
        Canvg.from (
          ctx,
          serializer.serializeToString(svgNode),
          {
            ignoreMouse: true,
            ignoreAnimation: true,
          },
        ).then((v) => {
          v.start();
        });
        if (callback) {
          callback();
        }
      } else if (callback) {
        callback();
      }
    }
  };

  contents.shiftLines = (chartObj, yValue) => {
    chartObj.selectAll('.line-area').attr('transform', `translate(0,  ${yValue})`);
  };
  contents.getEndAngle = (cld) => {
    let endAngleValue;
    switch (cld) {
      case DefConst.clouds.clear:
        // clear
        endAngleValue = utilityService.degreeToRadian(0);
        break;
      case DefConst.clouds.few:
        // few
        endAngleValue = utilityService.degreeToRadian(90);
        break;
      case DefConst.clouds.partial:
      case DefConst.clouds.scattered:
        // scattered
        endAngleValue = utilityService.degreeToRadian(180);
        break;
      case DefConst.clouds.broken:
        // broken
        endAngleValue = utilityService.degreeToRadian(270);
        break;
      case DefConst.clouds.overcast:
        // overcast
        endAngleValue = utilityService.degreeToRadian(360);
        break;
      default:
    }
    return endAngleValue || 0;
  };
  contents.addChartLabels = (
    chartObj,
    normalTextHeight,
    width,
    chartLabelSetting,
    attachNoDataPrefix,
  ) => {
    const chartLabel = chartObj.append('g')
      .attr('id', 'chartLabel');
    chartLabel.append('text')
      .text(() => chartLabelSetting.label + (attachNoDataPrefix === true
        ? chartLabelSetting.noDataPostfix
        : ''))
      .attr('class', 'shadow-text')
      .attr('x', 1)
      .attr('y', normalTextHeight - 1);
    chartLabel.attr('transform', () => {
      const x = (width / 2) - (calcSelectionWidth(chartLabel.select('text')) / 2);
      return `translate(${x}, 0)`;
    });
  };

  contents.addNoSeriesDataText = (
    chartObj,
    chartData,
    chartHeight,
    width,
    yAxisRectWidth,
    displayText,
    noDataForAloftAvailable,
  ) => {
    if (chartData.length === 0 || noDataForAloftAvailable === true) {
      chartObj.append('g')
        .append('text')
        .attr('class', (noDataForAloftAvailable
          ? 'no-aloft-to-show'
          : 'no-series-to-show'))
        .text(displayText)
        .attr('x', (...args) => {
          const selfEle = utilityService.getSelfElement(args);
          const x = yAxisRectWidth + ((width - calcSelectionWidth(d3.select(selfEle))) / 2);
          return x;
        })
        .attr('y', (...args) => {
          const selfEle = utilityService.getSelfElement(args);
          const y = (chartHeight / 2) + (calcSelectionHeight(d3.select(selfEle)) / 3);
          return y;
        });
    }
  };

  function drawNightBar(sunset, sunrise, barheight, dayNightIndicatorBar, x, width) {
    let widthOfNightBar = (x(sunrise) - x(sunset)) < 0
      ? 0
      : (x(sunrise) - x(sunset));
    let x1;
    let x2;
    const xSunset = x(sunset);
    const xSunRise = x(sunrise);
    /*  [x1]=======nightBar=======[x2]  */
    if ((xSunset < 0 && xSunRise < 0) || (xSunset > width && xSunRise > width)) {
      return null;
    }
    if (xSunset < 0 && (xSunRise > 0 && xSunRise < width)) {
      x1 = 0;
      x2 = xSunRise;
    } else if ((xSunset > 0 && xSunset < width) && xSunRise > width) {
      x1 = xSunset;
      x2 = width;
    } else {
      x1 = xSunset;
      x2 = xSunRise;
    }
    widthOfNightBar = x2 - x1;
    if (widthOfNightBar >= 0) {
      dayNightIndicatorBar.append('rect')
        .attr('transform', () => `translate(${roundOfNumber(x1)},  0)`)
        .attr('width', () => roundOfNumber(widthOfNightBar))
        .attr('height', roundOfNumber(barheight))
        .attr('fill', () => '#bbbbbb')
        .attr('stroke', ChartConst.gridView.borderColor)
        .attr('stroke-width', ChartConst.gridView.borderWidth);
      return {
        sunset,
        sunrise,
      };
    }
    return null;
  }

  contents.drawDayNightIndicator = (
    barheight,
    dayNightIndicatorBar,
    x,
    width,
    daysForDayNight,
  ) => {
    let i = 0;
    const dayNightsTime = [];
    let temp;
    dayNightIndicatorBar.append('rect')
      .attr('width', () => roundOfNumber(width))
      .attr('height', roundOfNumber(barheight))
      .attr('fill', () => '#fff4c4')
      .attr('stroke', ChartConst.gridView.borderColor)
      .attr('stroke-width', ChartConst.gridView.borderWidth);
    if (daysForDayNight) {
      while (i < daysForDayNight.length) {
        if (i < daysForDayNight.length - 1) {
          temp = drawNightBar(
            daysForDayNight[i].sunMoonPositionData.todaysSunlightTime.sunset,
            daysForDayNight[i + 1].sunMoonPositionData.todaysSunlightTime.sunrise,
            barheight,
            dayNightIndicatorBar,
            x,
            width,
          );
          if (temp) {
            dayNightsTime.push(temp.sunrise);
            dayNightsTime.push(temp.sunset);
          }
        }
        i += 1;
      }
    }
    return dayNightsTime;
  };

  function calculateHeadWind(windSpeed, windDirection, flightDirection) {
    const alpha = windDirection - flightDirection;
    const headWind = windSpeed * Math.cos(utilityService.degreeToRadian(alpha));
    return parseFloat(headWind.toFixed(0));
  }

  function barbObjectForZeroWind(barbData) {
    barbData.d = ChartConst.aloftZeroWindConfig.pathValue;
    barbData.transform = ChartConst.aloftZeroWindConfig.transform;
    return barbData;
  }

  function doALoftWindCalc(d, defaultWindSpeed, flightDirection, showHeadWindOnly) {
    let windSpeed = utilityService.getAloftWindSpeed(d.ugrd, d.vgrd);
    let windDirection = utilityService.getAloftWindDirection(d.ugrd, d.vgrd);
    const windData = {
      windSpeedOrg: windSpeed,
      windDirectionOrg: windDirection,
    };
    let headWind = null;
    if (showHeadWindOnly) {
      windDirection *= 10;
      headWind = calculateHeadWind(windSpeed, windDirection, flightDirection);
      windSpeed = Math.abs(headWind);
    }
    const barbData = windbarbService.getWindBarbConfig(
      windSpeed,
      windDirection,
      defaultWindSpeed.arrowWidth,
    );
    if (!barbData.d) {
      barbObjectForZeroWind(barbData);
    }
    windData.windSpeed = windSpeed;
    windData.windDirection = windDirection;
    windData.barbData = barbData;
    windData.headWind = headWind;
    // If ShowHeadWindOnly true and headwind is less the 0 the its tailwind
    if (showHeadWindOnly && headWind < 0) {
      windData.barbData.transform.rotate = 0;
      windData.headWindPrefix = ChartConst.aloftBarbTooltip.tailwind;
    } else if (showHeadWindOnly && headWind >= 0) {
      windData.barbData.transform.rotate = 180;
      windData.headWindPrefix = ChartConst.aloftBarbTooltip.headwind;
    }
    return windData;
  }
  contents.aloftAltNotNull = (alt) => {
    if (alt.ugrd !== null && alt.vgrd !== null) {
      return true;
    }
    return false;
  };
  contents.isValuePresent = (value) => {
    if (value === null || value === undefined) {
      return false;
    }
    return true;
  };
  contents.drawAloftWindBarbsTooltips = (
    ceilingMinValueAllowed,
    ceilingMaxValueAllowed,
    exactMatchData,
    aloftAltitudeMap,
    aloftWindBarbsContainer,
    x,
    y1,
    xKey,
    viewPrefix,
    flightDots,
    defaultWindSpeed,
    showHeadWindOnly,
    settings,
    normalTextHeight,
  ) => {
    const aloftBarbCol = aloftWindBarbsContainer.selectAll('col')
      .data(exactMatchData)
      .enter()
      .append('g')
      .attr('class', (...args) => {
        const i = args[1];
        return `aloft-barb-col aloft-barb-col-${i + 1}`;
      })
      .attr('data', (d) => d[xKey])
      .attr('datakey', (...args) => {
        const key = args[1];
        return key; // DAB shift one column to the right
      })
      .attr('transform', (d) => {
        const xValue = x(d[xKey]); // DAB shift one column to the right
        return `translate(${xValue}, 0)`;
      })
      .attr('tooltipWestGravity', (d) => !!(x(d[xKey]) > (window.innerWidth / 2)));

    aloftBarbCol.selectAll('path .row')
      .data((d) => {
        const altsToDraw = d.altitudes.filter((...args) => {
          const alt = args[0];
          // Filter out values where CIG is above max allowed CIG
          const idx = args[1];
          return (contents.aloftAltNotNull(alt)
            && (DefConst[DefConst.aloftCloudIndexMapKey + settings.dataMode][idx]
              >= ceilingMinValueAllowed));
        });
        return altsToDraw.filter((...args) => {
          const idx = args[1];
          return aloftAltitudeMap[idx] < ceilingMaxValueAllowed;
        });
      })
      .enter();

    const tooltipG = aloftBarbCol.selectAll('tool .row')
      .data((d) => {
        const altsToDraw = d.altitudes.filter((...args) => {
          // Filter out values where CIG is above max allowed CIG
          const alt = args[0];
          const idx = args[1];
          return (contents.aloftAltNotNull(alt)
            && (DefConst[DefConst.aloftCloudIndexMapKey + settings.dataMode][idx]
              >= ceilingMinValueAllowed));
        });
        return altsToDraw.filter((...args) => {
          const idx = args[1];
          return aloftAltitudeMap[idx] < ceilingMaxValueAllowed;
        });
      })
      .enter()
      .append('g')
      .attr('class', (...args) => {
        const key = args[1];
        const selfEle = utilityService.getSelfElement(args);
        const datakey = d3.select(selfEle.parentNode).attr('datakey');
        return `${viewPrefix}aloft-barb ${viewPrefix}aloft-barb-group-${key}-${datakey}`;
      });
    if (utilityService.isFirefoxOrChrome()) {
      tooltipG.style('opacity', '0');
    } else {
      tooltipG.style('display', 'none');
    }
    tooltipG.append('rect')
      .attr('fill', ChartConst.aloftBarbTooltip.rectColor)
      .attr('stroke', ChartConst.aloftBarbTooltip.rectColor)
      .attr('y', 0);
    const textNode = tooltipG.append('text').text((d) => {
      const windData = doALoftWindCalc(
        d,
        defaultWindSpeed,
        d.flightDirection,
        showHeadWindOnly,
      );
      const windDirectionText = `00${windData.windDirection.toFixed(0) * 10}`;
      let tooltiptext = `${ChartConst.ChartLabelSpecs.wsp.tooltip.prefix}: `;
      let tspanText = '';
      tspanText = `${windDirectionText.slice(-3) + ChartConst.ChartLabelSpecs.wdr.tooltip.unit} ${windData.windSpeed.toFixed(0)}${ChartConst.ChartLabelSpecs.wsp.tooltip.unit}`;
      if (windData.headWind !== null) {
        tooltiptext = `${windData.headWindPrefix}: `;
        tspanText = windData.windSpeed.toFixed(0) + ChartConst.ChartLabelSpecs.wsp.tooltip
          .unit;
      }
      if (!tspanText) {
        tspanText = '--';
      }
      d.tspanText = tspanText;
      return tooltiptext;
    })
      .attr('x', 2)
      .attr('fill', ChartConst.aloftBarbTooltip.textColor);
    const textNodeTmp = tooltipG.append('text').text((d) => {
      d.tmpPresent = contents.isValuePresent(d.tmp);
      if (d.tmpPresent) {
        const tooltiptext = `${ChartConst.ChartLabelSpecs.tmp.tooltip.prefix}: `;
        if (settings.temperatureUnit === AppConst.temperatureUnits.fahrenheit.key) {
          d.tmp_display_value = d.tmp.toFixed(0);
        } else {
          d.tmp_display_value = utilityService.fahrenheitToCelsius(d.tmp).toFixed(0);
        }
        d.tspanTextTmp = getTempLabelValue(
          settings.temperatureUnit,
          utilityService.fahrenheitToCelsius(d.tmp),
          d.tmp,
        );
        if (d.tspanTextTmp.indexOf('NaN') > -1) {
          d.tspanTextTmp = '--';
        }
        return tooltiptext;
      }
      return null;
    })
      .attr('x', 2)
      .attr('fill', ChartConst.aloftBarbTooltip.textColor);
    textNode.append('tspan').attr('class', 'boldtext').text((d) => d.tspanText);
    textNodeTmp.append('tspan').attr('class', 'boldtext').text((d) => d.tspanTextTmp);
    tooltipG.select('rect')
      .attr('width', (...args) => {
        const selfEle = utilityService.getSelfElement(args);
        const [windNode, tempNode] = d3.select(selfEle.parentNode).selectAll('text')._groups[0];
        const windNodeWidth = contents.getSelectionWidth(d3.select(windNode));
        const tempNodeWidth = contents.getSelectionWidth(d3.select(tempNode));
        return Math.max(windNodeWidth, tempNodeWidth) + 4;
      }).attr('height', (normalTextHeight * 2) - 2);

    tooltipG.attr('transform', (...args) => {
      const key = args[1];
      const selfEle = utilityService.getSelfElement(args);
      const barbLengthFactor = defaultWindSpeed.arrowWidth - defaultWindSpeed.tooltipOffset;
      let xValue = barbLengthFactor;
      const yValue = y1(aloftAltitudeMap[key]) - (normalTextHeight);
      const tooltipWestGravity = !!(d3.select(selfEle.parentNode).attr(
        'tooltipWestGravity',
      ) === 'true');
      if (tooltipWestGravity) {
        xValue = -(contents.getSelectionWidth(d3.select(selfEle).select('text')) + 4 + (
          flightDots.radius * 2) + (flightDots.strokeWidth * 2));
      }
      return `translate(${xValue}, ${yValue})`;
    });
    textNode.attr('y', (...args) => {
      const selfEle = utilityService.getSelfElement(args);
      const tooltipTextHeight = parseFloat(d3.select(selfEle.parentNode).select('text')
        .style('font-size'));
      textNodeTmp.attr('y', (tooltipTextHeight - 0.5) * 2);
      return tooltipTextHeight - 0.5;
    });
    if (utilityService.isFirefoxOrChrome()) {
      tooltipG.style('opacity', '1');
      tooltipG.style('display', 'none');
    }
  };
  contents.drawAloftWindBarbs = (
    ceilingMinValueAllowed,
    ceilingMaxValueAllowed,
    exactMatchData,
    aloftAltitudeMap,
    defaultWindSpeed,
    aloftWindBarbsContainer,
    x,
    y1,
    xKey,
    viewPrefix,
    showHeadWindOnly,
    settings,
  ) => {
    const aloftBarbCol = aloftWindBarbsContainer.selectAll('col')
      .data(exactMatchData)
      .enter()
      .append('g')
      .attr('class', (...args) => {
        const i = args[1];
        return `aloft-barb-col aloft-barb-col-${i + 1}`;
      })
      .attr('data', (d) => d[xKey])
      .attr('datakey', (...args) => {
        const key = args[1];
        return key; // DAB shift one column to the right
      })
      .attr('transform', (d) => {
        const xValue = x(d[xKey]); // DAB shift one column to the right
        return `translate(${xValue}, 0)`;
      });

    aloftBarbCol.selectAll('path .row')
      .data((d) => {
        const altsToDraw = d.altitudes.filter((...args) => {
          const alt = args[0];
          // Filter out values where CIG is above max allowed CIG
          const idx = args[1];
          return (contents.aloftAltNotNull(alt)
            && (DefConst[DefConst.aloftCloudIndexMapKey + settings.dataMode][idx]
              >= ceilingMinValueAllowed));
        });
        return altsToDraw.filter((...args) => {
          const key = args[1];
          return aloftAltitudeMap[key] < ceilingMaxValueAllowed;
        });
      })
      .enter()
      .append('path')
      .attr('marker-start', ConfigApplicationSettings.settings.showBarbHead
        ? `url(#${viewPrefix}${defaultWindSpeed.barbHead}-aloft)`
        : '')
      .attr('stroke', defaultWindSpeed.color)
      .attr('stroke-width', defaultWindSpeed.width)
      .attr('class', (...args) => {
        const d = args[0];
        const key = args[1];
        const selfEle = utilityService.getSelfElement(args);
        const datakey = d3.select(selfEle.parentNode).attr('datakey');
        d.flightDirection = exactMatchData[parseInt(datakey, 10)].flightDirection;
        const windData = doALoftWindCalc(
          d,
          defaultWindSpeed,
          d.flightDirection,
          showHeadWindOnly,
        );
        return `${viewPrefix}aloft-barb ${viewPrefix}barb-path ${viewPrefix}aloft-barb-group-${key} ${windData.barbData['barb-class']}`;
      })
      .attr('d', (d) => {
        const windData = doALoftWindCalc(
          d,
          defaultWindSpeed,
          d.flightDirection,
          showHeadWindOnly,
        );
        return windData.barbData.d;
      })
      .attr('transform', (d, key) => {
        const windData = doALoftWindCalc(
          d,
          defaultWindSpeed,
          d.flightDirection,
          showHeadWindOnly,
        );
        return `translate(0, ${y1(aloftAltitudeMap[key])}) scale(${windData.barbData.transform.scale}), rotate(${windData.barbData.transform.rotate}, 0, 0), translate(${windData.barbData.transform.translate.x}, ${windData.barbData.transform.translate.y})`;
      })
      .style('visibility', (...args) => {
        const idx = args[1];
        if (settings.dataMode === 'meteogram' && aloftAltitudeMap[idx] < settings
          .airportDetails.el) {
          return 'hidden';
        }
        return null;
      })
      .attr('stroke', ChartConst.aloftBarbInitialColor)
      .style('fill', ChartConst.aloftBarbInitialColor);
  };

  contents.processGroup2Tips = (group, refChartHeight, tooltipContainer) => {
    const minDist = ChartConst.GraphConfig.trackLine.minDistanceBtToolTip;
    const rectHeight = parseFloat(tooltipContainer.select('rect').attr('height'));
    const tooltipTextHeight = parseFloat(tooltipContainer.select('text').style('font-size'));
    let i;
    const additionFactor = utilityService.is_small_device()
      ? ChartConst.GraphConfig.trackLine.mobileOffsetMinDistanceToolTip
      : ChartConst.GraphConfig.trackLine.OffsetMinDistanceToolTip + 0.5;
    let minDistFactor;
    group.sort((a, b) => a.y - b.y);
    _forEach(group, (...args) => {
      const groupItemIndex = args[1];
      let lastRectY;
      group[groupItemIndex].yOriginal = utilityService.clone(group[groupItemIndex].y);
      if (groupItemIndex === 0) {
        // If first value is towards top then push it down
        if (group[groupItemIndex].y < (tooltipTextHeight / 2)) {
          group[groupItemIndex].off = (group[groupItemIndex].y
            + (tooltipTextHeight
              - group[groupItemIndex].y));
          group[groupItemIndex].y += (group[groupItemIndex].y
            + (tooltipTextHeight - group[groupItemIndex].y));
        }
      }
      if (groupItemIndex > 0) {
        lastRectY = group[groupItemIndex - 1].y;
        group[groupItemIndex].off = Math.max(
          0,
          (lastRectY + rectHeight + minDist) - group[groupItemIndex].y,
        );
        group[groupItemIndex].y += group[groupItemIndex].off;
      }
    });
    i = group.length - 1;
    // TBD : Refactor this code
    while (i >= 0) {
      if (i === group.length - 1) {
        minDistFactor = minDist * (additionFactor + 0.5);
        if ((group[i].y + (tooltipTextHeight / 2)) > refChartHeight) {
          group[i].off = -(
            (tooltipTextHeight / 2)
            + (minDistFactor)
            - (refChartHeight - group[i].yOriginal));
          group[i].y = group[i].yOriginal + group[i].off;
        }
      } else {
        minDistFactor += additionFactor;
        if ((group[i].y + (tooltipTextHeight / 2)) > refChartHeight) {
          group[i].off = -(((tooltipTextHeight
                * (group.length - 1 - i))
              + tooltipTextHeight / 2)
            + (minDistFactor)
            - (refChartHeight - group[i].yOriginal));
          group[i].y = group[i].yOriginal + group[i].off;
        } else if ((group[i].y + (tooltipTextHeight)) > group[i + 1].y) {
          group[i].off = -(((tooltipTextHeight
                * (group.length - 1 - i))
              + tooltipTextHeight / 2)
            + (minDistFactor)
            - (refChartHeight - group[i].yOriginal));
          group[i].y = group[i].yOriginal + group[i].off;
        }
      }
      i -= 1;
    }

    group.sort((a, b) => a.ind - b.ind);
    return group;
  };

  contents.processGroupTips = (group, chartHeight, tooltipContainer) => {
    const minDist = ChartConst.GraphConfig.trackLine.minDistanceBtToolTip;
    const rectHeight = parseFloat(tooltipContainer.select('rect').attr('height'));
    const tooltipTextHeight = parseFloat(tooltipContainer.select('text').style('font-size'));
    group.sort((a, b) => a.y - b.y);
    _forEach(group, (...args) => {
      let lastRectY;
      const i = args[1];
      if (i === 0) {
        // If first value is towards top then push it down
        if (group[i].y < tooltipTextHeight) {
          group[i].off = (group[i].y + (tooltipTextHeight - group[i].y));
          group[i].y += (group[i].y + (tooltipTextHeight - group[i].y));
        }
        // If first value is towards bottom then lift it up
        if (group[i].y > (chartHeight - tooltipTextHeight)) {
          group[i].off = -(group[i].y + (rectHeight - group[i].y));
          group[i].y -= (group[i].y + (rectHeight - group[i].y));
        }
      }
      if (i > 0) {
        lastRectY = group[i - 1].y;
        group[i].off = Math.max(0, (lastRectY + rectHeight + minDist) - group[i].y);
        group[i].y += group[i].off;
        // If second tooltip is outside chart bottom border
        if ((group[i].y + (tooltipTextHeight / 2)) > chartHeight) {
          group[i].off -= (tooltipTextHeight / 2);
          group[i].y -= group[i].off;
          // If first tooltip collided with second tooltip then lift first one up
          if ((group[i].y - group[i - 1].y) < (tooltipTextHeight * 2)) {
            group[i - 1].off -= (tooltipTextHeight / 2);
          }
        }
      }
    });

    group.sort((a, b) => a.ind - b.ind);
    return group;
  };
  contents.alignToolTips = (tipsGroupClass, ypos, tooltipContainer, tooltipWestGravity) => {
    const tooltipTextHeight = parseFloat(tooltipContainer.select('text').style('font-size'));
    // Text Positioning
    d3.selectAll(tipsGroupClass).select('text').attr('transform', (...args) => {
      let x;
      let y;
      const d = args[0];
      const i = args[1];
      const selfEle = utilityService.getSelfElement(args);
      if (ypos[`group${d.group}`][i]) {
        x = (tooltipWestGravity
          ? (-(calcSelectionWidth(d3.select(selfEle)) + 8))
          : (tooltipTextHeight / 2) + 2);
        y = (4 + ypos[`group${d.group}`][i].off);
        return `translate (${x},${y})`;
      }
      return undefined;
    });
    // Rect Positioning
    d3.selectAll(tipsGroupClass).select('rect').attr('transform', (...args) => {
      let x;
      let y;
      const d = args[0];
      const i = args[1];
      const selfEle = utilityService.getSelfElement(args);
      if (ypos[`group${d.group}`][i]) {
        x = (tooltipWestGravity
          ? (-(parseFloat(d3.select(selfEle).attr('width')) + (tooltipTextHeight / 2)))
          : (tooltipTextHeight / 2));
        y = (5 + ypos[`group${d.group}`][i].off - tooltipTextHeight);
        return `translate (${x},${y})`;
      }
      return undefined;
    });
  };

  contents.getNextCurrentPrevX = (refValues, x, currentItem, refProp, key) => {
    // refValues : array of source data
    // x: d3 x axis function
    // currentItem: d
    // refProp: property that contain dateTime of x-axis
    let prevX;
    let nextX;
    const currentX = x(currentItem[refProp]);
    if (key === 0 && refValues[key + 1]) {
      prevX = currentX;
      nextX = x(refValues[key + 1][refProp]);
    } else if (key === refValues.length - 1 && refValues[key - 1]) {
      prevX = x(refValues[key - 1][refProp]);
      nextX = currentX;
    } else if (refValues[key - 1] && refValues[key + 1]) {
      prevX = x(refValues[key - 1][refProp]);
      nextX = x(refValues[key + 1][refProp]);
    }
    return {
      prevX,
      currentX,
      nextX,
    };
  };

  contents.getMaxTextWidth = (element) => {
    let i = 0;
    const widthArrays = [];

    function filterEle(...args) {
      return args[1] === i;
    }
    while (i < element.selectAll('text').size()) {
      widthArrays.push(calcSelectionWidth(element.selectAll('text').filter(filterEle)));
      i += 1;
    }
    const maxWidth = Math.max.apply(Object, widthArrays);
    return maxWidth;
  };

  function checkIfSameTypeOfHazard(airmetPrimary, airmetSecondary) {
    if (airmetPrimary.hazard_type === airmetSecondary.hazard_type) {
      return true;
    }
    if (airmetPrimary.hazard_type === ChartConst.gairmetConfig.mtnObscn.key
      || airmetPrimary.hazard_type === ChartConst.gairmetConfig.mtnObscn.key2) {
      return true;
    }
    if (airmetPrimary.hazard_type === ChartConst.gairmetConfig.sfcWinds.key
      || airmetPrimary.hazard_type === ChartConst.gairmetConfig.sfcWinds.key2) {
      return true;
    }
    return false;
  }

  function checkIfSameAirmetOverlapping(airmetDataCollection, airmet) {
    let alreadyExist = false;
    let i = 0;
    let cAirmet;
    while (i < airmetDataCollection.length) {
      cAirmet = airmetDataCollection[i];
      if (checkIfSameTypeOfHazard(airmet, cAirmet)
        && cAirmet.rectWidth === airmet.rectWidth
        && cAirmet.rectHeight === airmet.rectHeight
        && cAirmet.xPosition === airmet.xPosition
        && cAirmet.yPosition === airmet.yPosition) {
        alreadyExist = true;
        break;
      }
      i += 1;
    }
    return alreadyExist;
  }

  function rectContains(r1, r2) {
    let contains = false;
    const areaOfR1 = r1.rectHeight * r1.rectWidth;
    const areaOfR2 = r2.rectHeight * r2.rectWidth;
    if (areaOfR1 <= areaOfR2) {
      if (r1.xPosition >= r2.xPosition
        && ((r1.xPosition + r1.rectWidth) <= (r2.xPosition + r2.rectWidth))) {
        if (r1.yPosition >= r2.yPosition
          && ((r1.yPosition + r1.rectHeight) <= (r2.yPosition + r2.rectHeight))) {
          contains = true;
        }
      }
    }
    if (contains && (areaOfR1 === areaOfR2) && (!r1.contained && !r2.contained)) {
      // Exact overlapping and no rect is already marked contained
      contains = false;
    }
    return contains;
  }
  contents.mergeAirmetOverlappings = (airmetDataCollection) => {
    let i = 0;
    let j;
    let rectA;
    let rectB;

    while (i < airmetDataCollection.length) {
      rectA = airmetDataCollection[i];
      j = i + 1;
      while (j < airmetDataCollection.length) {
        rectB = airmetDataCollection[j];
        if (j !== i) {
          if (rectA.hazardConfig.strokeColor === rectB.hazardConfig.strokeColor) {
            if (rectA.rectHeight === rectB.rectHeight
              && rectA.yPosition === rectB.yPosition
              && ((rectB.xPosition - (rectA.xPosition + rectA.rectWidth)) < 2)) {
              rectB.isMerging = true;
              rectA.rectWidth = (rectB.xPosition - rectA.xPosition) + rectB.rectWidth;
              rectA.right_edge = rectB.right_edge;
              rectA[rectA.x2Key] = rectB[rectB.x2Key];
            }
          }
        }
        j += 1;
      }
      i += 1;
    }
  };
  contents.filterContainedRect = (airmetDataCollection) => {
    let i = 0;
    let j;
    let rectA;
    let rectB;
    // desanitize airmets
    while (i < airmetDataCollection.length) {
      airmetDataCollection[i].contained = false;
      airmetDataCollection[i].isMerging = false;
      i += 1;
    }
    i = 0;
    while (i < airmetDataCollection.length) {
      rectA = airmetDataCollection[i];
      j = 0;
      while (j < airmetDataCollection.length) {
        rectB = airmetDataCollection[j];
        if (j !== i) {
          if (rectA.hazardConfig.strokeColor === rectB.hazardConfig.strokeColor) {
            if (rectContains(rectA, rectB)) {
              rectA.contained = true;
            } else {
              rectA.contained = false;
            }
          }
        }
        j += 1;
      }
      i += 1;
    }
    if (airmetDataCollection) {
      // Remove airmet rect that are contained inside similar airmet
      _forEach(airmetDataCollection.slice().reverse(), (item, index, object) => {
        if (item.contained) {
          airmetDataCollection.splice(object.length - 1 - index, 1);
        }
      });
    }
    // Sort by priority
    if (airmetDataCollection && airmetDataCollection.length) {
      airmetDataCollection.sort((a, b) => a.xPosition - b.xPosition);
      contents.mergeAirmetOverlappings(airmetDataCollection);
      _forEach(airmetDataCollection.slice().reverse(), (item, index, object) => {
        if (item.isMerging) {
          airmetDataCollection.splice(object.length - 1 - index, 1);
        }
      });
      airmetDataCollection.sort((a, b) => a.hazardConfig.priority - b.hazardConfig.priority);
    }
    return airmetDataCollection;
  };
  contents.getCoordinateAtPointAlongPath = (path, point) => {
    let p = {
      x: 0,
      y: 0,
    };
    // console.log(point);
    if (path) {
      p = path.getPointAtLength(point);
    }
    // console.log("444444", path.getTotalLength());

    return p;
  };
  contents.getDataForTmpLowerBound = (path, interval, xStart, xEnd) => {
    const cloudsData = [];
    if (path && path.node()) {
      // parseInt(d3.select("#y-axis-rect-left").attr("width"));
      const yOffsetToConsiderYAxisLeft = 0;
      let i = xStart - yOffsetToConsiderYAxisLeft;
      let l = xEnd - yOffsetToConsiderYAxisLeft;
      const totalLengthOfPath = path.node().getTotalLength();
      let k = 0;
      let segmentStart;
      let segmentEnd;
      let tempXContainer;
      while (k < totalLengthOfPath) {
        tempXContainer = contents.getCoordinateAtPointAlongPath(path.node(), k);
        if (tempXContainer.x >= xStart) {
          segmentStart = k;
          break;
        }
        k += 1;
      }
      k = segmentStart;
      while (k < totalLengthOfPath) {
        tempXContainer = contents.getCoordinateAtPointAlongPath(path.node(), k);
        if (tempXContainer.x > xEnd) {
          segmentEnd = k;
          break;
        }
        k += 1;
      }
      i = segmentStart;
      // If segmentStart has a value but segmentEnd is undefined
      // That means airmet is across whole width
      if (segmentStart && !segmentEnd) {
        segmentEnd = totalLengthOfPath;
      }
      l = segmentEnd;
      while (i < l) {
        cloudsData.push(contents.getCoordinateAtPointAlongPath(path.node(), i).y);
        i += interval;
      }
    }
    return Math.max.apply(Object, cloudsData);
  };

  function getCorrectFreezeValue(airmet, tmpDataLowerBoundPath, yAxis) {
    let lowestFreeze;
    if (airmet.hazard_type === ChartConst.gairmetConfig.icing.key
      && airmet.altitude_min_original === -1) {
      lowestFreeze = contents.getDataForTmpLowerBound(
        tmpDataLowerBoundPath,
        2,
        airmet.xPosition,
        airmet.xPosition + airmet.rectWidth,
      );
      // console.log(lowestFreeze, yAxis.invert(lowestFreeze));
      airmet.altitude_min = yAxis.invert(lowestFreeze);
      // if (settings && settings.airportDetails) {
      // airmet.altitude_min += settings.airportDetails.el;
      // }
    }
  }

  function getCorrectAglValue(airmet, elvX, elevationData) {
    let i = 0;
    const validChunk = [];
    let elvSampleY;
    if (airmet.isAglAirmet && elevationData) {
      while (i < elevationData.length) {
        elvSampleY = elvX(i);
        if (elvSampleY >= airmet.left_edge && elvSampleY <= airmet.right_edge) {
          validChunk.push(elevationData[i]);
        }
        // Move ahead by elevationJumpsForAirmet px to get airmet min as per elevation
        i += ChartConst.elevationJumpsForAirmet;
      }

      airmet.minElvInSpread = Math.min.apply(null, validChunk.map((o) => o.elevation));
      airmet.maxElvInSpread = Math.max.apply(null, validChunk.map((o) => o.elevation));
      if (airmet.minElvInSpread) {
        airmet.altitude_min = airmet.minElvInSpread;
      }
      if (airmet.maxElvInSpread) {
        airmet.altitude_max = airmet.maxElvInSpread + airmet.altitude_max;
      }
    }
  }

  contents.calcAirmetPositioningParametersRoute = (
    airmetData,
    x1Key,
    x2Key,
    y1Key,
    y2Key,
    xAxis,
    nmXAxis,
    yAxis,
    chartRangeLowerBoundDate,
    collectionType,
    airmetDataCollection,
    waitForAloft,
    aloftData,
    settings,
    fullData,
    elvX,
    tmpDataLowerBoundPath,
  ) => {
    _forEach(airmetData, (airmet) => {
      // Resetting altitude_max to handle dynamic ceiling value
      airmet[y2Key] = Math.min(settings.maximumAltitudeAllowed, airmet
        .altitude_max_org_ref);
      airmet.start_time = xAxis(airmet[x1Key]);
      airmet.start_dist = nmXAxis(airmet.start_nm);
      airmet.end_time = xAxis(airmet[x2Key]);
      airmet.end_dist = nmXAxis(airmet.end_nm);
      airmet.right_edge = null;
      airmet.left_edge = null;
      if (airmet.start_time >= airmet.start_dist) {
        airmet.left_edge = airmet.start_time;
      } else {
        airmet.left_edge = airmet.start_dist;
      }
      if (airmet.end_time <= airmet.end_dist) {
        airmet.right_edge = airmet.end_time;
      } else {
        airmet.right_edge = airmet.end_dist;
      }
      getCorrectAglValue(airmet, elvX, fullData.elevationData);
      airmet.xPosition = airmet.left_edge + 1;
      // Keep value positive because
      airmet.rectWidth = Math.max(airmet.right_edge - airmet.left_edge, 0);
      airmet.yPosition = yAxis(Math.min(settings.maximumAltitudeAllowed, airmet[y2Key]));
      if (waitForAloft && aloftData) {
        getCorrectFreezeValue(airmet, tmpDataLowerBoundPath, yAxis);
      }
      airmet.rectHeight = Math.abs(
        yAxis(
          Math.min(settings.maximumAltitudeAllowed, airmet[y2Key]),
        ) - yAxis(airmet[y1Key]),
      );
      airmet.rectArea = airmet.rectHeight * airmet.rectWidth;
      airmet.hazardConfig = bnChartDataProcessService.getGairmetColor(airmet);
      airmet.validAirmet = (airmet[x1Key] < airmet[x2Key]
        && airmet[x2Key] >= chartRangeLowerBoundDate);
      airmet.collectionType = collectionType;
      airmet.x1Key = x1Key;
      airmet.x2Key = x2Key;
      if (airmet[y1Key] >= settings.maximumAltitudeAllowed || airmet.rectWidth === 0) {
        airmet.validAirmet = false;
      }
      if (checkIfSameAirmetOverlapping(airmetDataCollection, airmet)) {
        airmet.validAirmet = false;
        airmet.hazardConfig.strokeColor = 'transparent';
      }
      if (airmet.validAirmet) {
        airmetDataCollection.push(airmet);
      }
    });
    return airmetDataCollection;
  };

  contents.calcAirmetPositioningParameters = (
    airmetData,
    x1Key,
    x2Key,
    y1Key,
    y2Key,
    xAxis,
    yAxis,
    chartRangeLowerBoundDate,
    collectionType,
    airmetDataCollection,
    waitForAloft,
    aloftData,
    settings,
    tmpDataLowerBoundPath,
  ) => {
    _forEach(airmetData, (airmet) => {
      // Resetting altitude_max to handle dynamic ceiling value
      airmet[y2Key] = Math.min(settings.maximumAltitudeAllowed, airmet
        .altitude_max_org_ref);
      airmet.xPosition = xAxis(Math.max(chartRangeLowerBoundDate, airmet[x1Key])) + 1;
      airmet.yPosition = yAxis(Math.min(settings.maximumAltitudeAllowed, airmet[y2Key]));
      airmet.rectWidth = Math.abs(
        xAxis(airmet[x2Key]) - xAxis(
          Math.max(chartRangeLowerBoundDate, airmet[x1Key]),
        ),
      );
      airmet.rectHeight = Math.abs(
        yAxis(Math.min(
          settings.maximumAltitudeAllowed,
          airmet[y2Key],
        )) - yAxis(
          airmet[y1Key],
        ),
      );
      if (waitForAloft && aloftData) {
        getCorrectFreezeValue(airmet, tmpDataLowerBoundPath, yAxis, settings);
      }
      airmet.rectHeight = Math.abs(
        yAxis(Math.min(settings.maximumAltitudeAllowed, airmet[y2Key])) - yAxis(airmet[
          y1Key]),
      );
      airmet.hazardConfig = bnChartDataProcessService.getGairmetColor(airmet);
      airmet.validAirmet = (airmet[x1Key] < airmet[x2Key]
        && airmet[x2Key] >= chartRangeLowerBoundDate);
      airmet.collectionType = collectionType;
      airmet.x1Key = x1Key;
      airmet.x2Key = x2Key;
      airmet.rectArea = airmet.rectHeight * airmet.rectWidth;
      if (airmet[y1Key] >= settings.maximumAltitudeAllowed) {
        airmet.validAirmet = false;
      }
      if (checkIfSameAirmetOverlapping(airmetDataCollection, airmet)) {
        airmet.validAirmet = false;
        airmet.hazardConfig.strokeColor = 'transparent';
      }
      if (airmet.validAirmet) {
        airmetDataCollection.push(airmet);
      }
    });
    return airmetDataCollection;
  };
  contents.attachLoader = (id) => {
    // d3.selectAll("#" + id + " svg").remove();
    const wrapper = document.getElementById(id);
    const radius = ChartConst.loaderRadius;
    const border = ChartConst.loaderTrackWidth;
    const strokeSpacing = ChartConst.loaderStrokeSpacing;
    const endAngle = Math.PI * 2;
    const formatText = d3.format('.0%');
    const boxSize = radius * 2;
    // Define the circle
    const circle = d3.arc()
      .startAngle(0)
      .innerRadius(radius)
      .outerRadius(radius - border);

    // setup SVG wrapper
    const svg = d3.select(wrapper)
      .append('svg')
      .attr('width', boxSize)
      .attr('height', boxSize);
    // console.log(svg);
    // ADD Group container
    const g = svg.append('g')
      .attr('transform', `translate(${boxSize / 2},${boxSize / 2})`);

    // Setup track
    const track = g.append('g').attr('class', 'radial-progress');
    track.append('circle')
      .attr('fill', ChartConst.loaderFillColor)
      .attr('r', radius - 6);

    // Add colour fill
    const value = track.append('path')
      .attr('class', 'radial-progress__value')
      .attr('fill', ChartConst.loaderTrackColor)
      .attr('stroke', ChartConst.loaderTrackColor)
      .attr('stroke-width', `${strokeSpacing}px`);

    // Add text value
    const numberText = track.append('text')
      .attr('class', 'radial-progress__text')
      .attr('fill', ChartConst.loaderTextColor)
      .attr('text-anchor', 'middle')
      .attr('dy', '8.5px');
    return {
      circle,
      numberText,
      value,
      formatText,
      endAngle,
    };
  };
  contents.drawAirmetAndGairmet = (airSigmetContainer, airmetData) => {
    const metLayersSelector = bnChartDataProcessService.getMetsForLayer();
    const airmetObj = airSigmetContainer.selectAll('group')
      .data(() => airmetData.filter((d) => d.validAirmet))
      .enter();
    // airmetObj.append("rect")
    //     .attr("clip-path", "url(#clip)")
    //     .attr("class", function (d) {
    //         if (d.isSigmet) {
    //             return "sigment-rects " + "metRect" + ChartConst.sigmetsLayerKey;
    //         }
    //         return "sigment-rects " + "metRect" + d.hazard_type_identifier.replace(" ", "");
    //     })
    //     .attr("hazard_type", function (d) {
    //         return d.hazard_type;
    //     })
    //     .attr("x", function (d) {
    //         return d.xPosition;
    //     })
    //     .attr("y", function (d) {
    //         return d.yPosition;
    //     })
    //     .attr("height", function (d) {
    //         return d.rectHeight;
    //     })
    //     .attr("width", function (d) {
    //         return d.rectWidth;
    //     })
    //     .style("stroke", function (d) {
    //         return d.hazardConfig.strokeColor;
    //     })
    //     .style("fill", function (d) {
    //         return d.hazardConfig.strokeColor;
    //     })
    //     .style("opacity", ChartConst.gairmetConfig.rectOpacity);
    airmetObj.append('rect')
      .attr('clip-path', 'url(#clip)')
      .attr('class', (d) => {
        if (d.isSigmet) {
          return `${'sigment-rects metRect'}${ChartConst.sigmetsLayerKey}`;
        }
        return `${'sigment-rects metRect'}${d.hazard_type_identifier.replace(' ', '')}`;
      })
      .attr('hazard_type', (d) => d.hazard_type)
      .attr('x', (d) => d.xPosition)
      .attr('y', (d) => d.yPosition)
      .attr('height', (d) => d.rectHeight)
      .attr('width', (d) => d.rectWidth)
      .style('stroke', (d) => d.hazardConfig.strokeColor)
      .style('stroke-width', ChartConst.gairmetConfig.strokeWidth)
      .style('fill', (d) => d.hazardConfig.strokeColor)
      .style('fill-opacity', ChartConst.gairmetConfig.rectOpacity);
    return contents.chartMetSuppress(metLayersSelector);
  };
  contents.getIfGairmetAndSigmentChecked = (mets) => {
    let isGairmetChecked = true;
    let isSigmetChecked = true;
    let i = 0;
    while (i < mets.length) {
      if (isGairmetChecked && mets[i].name === ChartConst.gairmetsLayerKey && !mets[i]
        .checked) {
        isGairmetChecked = false;
      }
      if (isSigmetChecked && mets[i].name === ChartConst.sigmetsLayerKey && !mets[i].checked) {
        isSigmetChecked = false;
      }
      i += 1;
    }
    return {
      isGairmetChecked,
      isSigmetChecked,
    };
  };
  contents.chartMetSuppress = (mets) => {
    const temp = contents.getIfGairmetAndSigmentChecked(mets);
    const {
      isGairmetChecked,
    } = temp;
    const {
      isSigmetChecked,
    } = temp;
    if (router.currentRoute.value.name === 'app.leafletmapview' || router.currentRoute.value
      .name
      === 'app.mapview') {
      Emitter.emit(EventConst.metchanged, {
        mets,
        isGairmetChecked,
      });
    } else {
      _forEach(mets, (met) => {
        if (met.name !== ChartConst.sigmetsLayerKey) {
          if (!met.checked || !isGairmetChecked) {
            d3.selectAll(`.metRect${met.name.replace(' ', '')}`).style('display', 'none');
          } else {
            d3.selectAll(`.metRect${met.name.replace(' ', '')}`).style(
              'display',
              'inherit',
            );
          }
        }
        if (met.name === ChartConst.sigmetsLayerKey && !isSigmetChecked) {
          d3.selectAll(`.metRect${met.name.replace(' ', '')}`).style('display', 'none');
        } else if (met.name === ChartConst.sigmetsLayerKey && isSigmetChecked) {
          d3.selectAll(`.metRect${met.name.replace(' ', '')}`).style('display', 'inherit');
        }
      });
    }
    return {
      isGairmetChecked,
      isSigmetChecked,
      mets,
    };
  };
  contents.aloftSuppress = () => {
    const suppressedMets = utilityService.getSuppressedMetFromLocalStorage();
    if (suppressedMets.indexOf(ChartConst.windsLayerKey) < 0) {
      d3.select('#aloft-canvas-container-meteo').style('display', 'block');
    } else if (suppressedMets.indexOf(ChartConst.windsLayerKey) > -1) {
      d3.select('#aloft-canvas-container-meteo').style('display', 'none');
    }
    if (suppressedMets.indexOf(ChartConst.windsLayerRouteKey) < 0) {
      d3.select('#aloft-canvas-container-route').style('display', 'block');
    } else if (suppressedMets.indexOf(ChartConst.windsLayerRouteKey) > -1) {
      d3.select('#aloft-canvas-container-route').style('display', 'none');
    }
  };
  return contents;
};
export default bnd3Service();
